import { yupResolver } from "@hookform/resolvers/yup";
import { LoadingButton } from "@mui/lab";
import {
    FormControl,
    FormHelperText,
    InputLabel,
    MenuItem,
    Modal,
    Select,
    TextField,
} from "@mui/material";
import dayjs from "dayjs";
import { useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import * as yup from "yup";

import { getApiErrorMessage } from "../../helpers/api/getApiErrorMessage";
import { formatBrlToNumber } from "../../helpers/formartBrlToNumber";
import {
    isValidDueDate,
    isValidInvoiceAmount,
    MIN_DUE_DATE_MESSAGE,
    MIN_INVOICE_AMOUNT_MESSAGE,
} from "../../helpers/invoice/invoiceValidation";
import {
    getInvoicePaymentMethodLabel,
    INVOICE_PAYMENT_METHOD_DEFAULT,
    INVOICE_PAYMENT_METHOD_OPTIONS,
} from "../../helpers/invoicePaymentMethods";
import { setMaskMoney } from "../../helpers/maskMoney";
import { defaultToast } from "../../helpers/toast/defaultToast";
import { api } from "../../services/api";
import ButtonGeneric from "../ButtonGeneric";
import { Container, ContentInput } from "../ModalMonthlyBilling/styles";

const schemaForm = yup
    .object({
        date_reference: yup.string().required("Campo obrigatório"),
        initial_date: yup.string().required("Campo obrigatório"),
        closing_date: yup.string().required("Campo obrigatório"),
        due_date: yup
            .string()
            .required("Campo obrigatório")
            .test("min-due-date", MIN_DUE_DATE_MESSAGE, (value) => {
                if (!value) {
                    return false;
                }

                return isValidDueDate(dayjs(value));
            }),
        name: yup.string().required("Campo obrigatório"),
        description: yup.string().required("Campo obrigatório"),
        value: yup
            .string()
            .required("Campo obrigatório")
            .test(
                "min-total-amount",
                MIN_INVOICE_AMOUNT_MESSAGE,
                (value) => {
                    if (!value) {
                        return false;
                    }

                    return isValidInvoiceAmount(formatBrlToNumber(value));
                }
            ),
        installments: yup.number().required("Campo obrigatório"),
        invoice_payment_methods: yup.string().nullable(),
    })
    .required();

type SchemaFormProps = yup.InferType<typeof schemaForm>;

interface Props {
    visible: boolean;
    setVisible: (data: boolean) => void;
    accountId: number | undefined;
    getFinancial: () => void;
    accountPaymentMethods?: string | null;
}

function ModalCreateMonthlyBilling({
    visible,
    setVisible,
    accountId,
    getFinancial,
    accountPaymentMethods = null,
}: Props) {
    const [isLoadingButton, setIsLoadingButton] = useState(false);
    const [globalPaymentMethods, setGlobalPaymentMethods] = useState(
        INVOICE_PAYMENT_METHOD_DEFAULT
    );

    const { control, handleSubmit, setValue, reset } = useForm<SchemaFormProps>(
        {
            resolver: yupResolver(schemaForm),
            defaultValues: {
                installments: 0,
                invoice_payment_methods: "",
            },
        }
    );

    const getDefaultFormDates = () => {
        const today = dayjs().format("YYYY-MM-DD");

        return {
            date_reference: today,
            initial_date: today,
            closing_date: today,
            due_date: today,
        };
    };

    useEffect(() => {
        if (!visible) {
            return;
        }

        const loadGlobalPaymentMethods = async () => {
            try {
                const response = await api.get("/settings");
                const groups = response.data.response ?? [];
                const settings = groups.flatMap(
                    (group: { settings?: Array<{ key: string; value: string }> }) =>
                        group.settings ?? []
                );
                const paymentSetting = settings.find(
                    (setting: { key: string }) =>
                        setting.key === "invoice_payment_methods"
                );

                if (paymentSetting?.value) {
                    setGlobalPaymentMethods(paymentSetting.value);
                }
            } catch {
                setGlobalPaymentMethods(INVOICE_PAYMENT_METHOD_DEFAULT);
            }
        };

        const defaultDates = getDefaultFormDates();

        loadGlobalPaymentMethods();
        setValue("date_reference", defaultDates.date_reference);
        setValue("initial_date", defaultDates.initial_date);
        setValue("closing_date", defaultDates.closing_date);
        setValue("due_date", defaultDates.due_date);
        setValue("invoice_payment_methods", "");
    }, [visible, setValue]);

    const defaultPaymentLabel = getInvoicePaymentMethodLabel(
        accountPaymentMethods || globalPaymentMethods
    );

    const handleClearDatas = () => {
        reset({
            ...getDefaultFormDates(),
            name: "",
            description: "",
            value: "",
            installments: 0,
            invoice_payment_methods: "",
        });
        setVisible(false);
    };

    const handleCreateMonthlyBilling = async (data: SchemaFormProps) => {
        if (!accountId) {
            defaultToast("error", "Conta de faturas não encontrada");
            return;
        }

        try {
            setIsLoadingButton(true);

            const amount = formatBrlToNumber(data.value);
            if (!isValidInvoiceAmount(amount)) {
                defaultToast("warning", MIN_INVOICE_AMOUNT_MESSAGE);
                return;
            }

            await api.post(`monthly-billing/generate/${accountId}`, {
                date_reference: data.date_reference,
                initial_date: data.initial_date,
                closing_date: data.closing_date,
                due_date: data.due_date,
                name: data.name,
                description: data.description,
                amount,
                installments: data.installments,
                invoice_payment_methods: data.invoice_payment_methods || null,
            });

            defaultToast("success", "Faturamento mensal criado com sucesso");
            handleClearDatas();
            getFinancial();
        } catch (error) {
            defaultToast(
                "error",
                getApiErrorMessage(
                    error,
                    "Não foi possível criar o faturamento mensal"
                )
            );
        } finally {
            setIsLoadingButton(false);
        }
    };

    return (
        <Modal open={visible} onClose={handleClearDatas}>
            <Container
                onSubmit={handleSubmit(handleCreateMonthlyBilling)}
                style={{ top: "50%", maxHeight: "90vh", overflowY: "auto" }}
            >
                <h1>Criar faturamento mensal</h1>

                <Controller
                    name="date_reference"
                    control={control}
                    render={({
                        field: { onChange, value },
                        fieldState: { error },
                    }) => (
                        <ContentInput>
                            <TextField
                                type="date"
                                onChange={onChange}
                                value={value || ""}
                                fullWidth
                                size="small"
                                label="Data de referência"
                                InputLabelProps={{ shrink: true }}
                                error={Boolean(error)}
                                helperText={error?.message}
                            />
                        </ContentInput>
                    )}
                />

                <Controller
                    name="initial_date"
                    control={control}
                    render={({
                        field: { onChange, value },
                        fieldState: { error },
                    }) => (
                        <ContentInput>
                            <TextField
                                type="date"
                                onChange={onChange}
                                value={value || ""}
                                fullWidth
                                size="small"
                                label="Data inicial"
                                InputLabelProps={{ shrink: true }}
                                error={Boolean(error)}
                                helperText={error?.message}
                            />
                        </ContentInput>
                    )}
                />

                <Controller
                    name="closing_date"
                    control={control}
                    render={({
                        field: { onChange, value },
                        fieldState: { error },
                    }) => (
                        <ContentInput>
                            <TextField
                                type="date"
                                onChange={onChange}
                                value={value || ""}
                                fullWidth
                                size="small"
                                label="Data de fechamento"
                                InputLabelProps={{ shrink: true }}
                                error={Boolean(error)}
                                helperText={error?.message}
                            />
                        </ContentInput>
                    )}
                />

                <Controller
                    name="due_date"
                    control={control}
                    render={({
                        field: { onChange, value },
                        fieldState: { error },
                    }) => (
                        <ContentInput>
                            <TextField
                                type="date"
                                onChange={onChange}
                                value={value || ""}
                                fullWidth
                                size="small"
                                label="Data de vencimento"
                                InputLabelProps={{ shrink: true }}
                                inputProps={{
                                    min: dayjs().format("YYYY-MM-DD"),
                                }}
                                error={Boolean(error)}
                                helperText={error?.message}
                            />
                        </ContentInput>
                    )}
                />

                <Controller
                    name="name"
                    control={control}
                    render={({
                        field: { onChange, value },
                        fieldState: { error },
                    }) => (
                        <ContentInput>
                            <TextField
                                onChange={onChange}
                                value={value || ""}
                                fullWidth
                                size="small"
                                label="Nome do item"
                                error={Boolean(error)}
                                helperText={error?.message}
                            />
                        </ContentInput>
                    )}
                />

                <Controller
                    name="description"
                    control={control}
                    render={({
                        field: { onChange, value },
                        fieldState: { error },
                    }) => (
                        <ContentInput>
                            <TextField
                                onChange={onChange}
                                value={value || ""}
                                fullWidth
                                size="small"
                                label="Descrição do item"
                                error={Boolean(error)}
                                helperText={error?.message}
                            />
                        </ContentInput>
                    )}
                />

                <Controller
                    name="value"
                    control={control}
                    render={({ field: { value }, fieldState: { error } }) => (
                        <ContentInput>
                            <TextField
                                onChange={(event) =>
                                    setValue(
                                        "value",
                                        String(setMaskMoney(event.target.value))
                                    )
                                }
                                fullWidth
                                size="small"
                                label="Valor"
                                value={value || ""}
                                error={Boolean(error)}
                                helperText={error?.message}
                            />
                        </ContentInput>
                    )}
                />

                <Controller
                    name="installments"
                    control={control}
                    render={({ field: { value }, fieldState: { error } }) => (
                        <ContentInput>
                            <FormControl fullWidth size="small">
                                <InputLabel id="create-billing-installments-label">
                                    Parcelas
                                </InputLabel>
                                <Select
                                    labelId="create-billing-installments-label"
                                    id="create-billing-installments"
                                    value={value ?? 0}
                                    label="Parcelas"
                                    onChange={(event) =>
                                        setValue(
                                            "installments",
                                            Number(event.target.value)
                                        )
                                    }
                                >
                                    <MenuItem value={0}>Sem parcela</MenuItem>
                                    {Array.from(Array(12).keys()).map(
                                        (installment) => (
                                            <MenuItem
                                                value={installment + 1}
                                                key={installment}
                                            >
                                                {installment + 1}
                                            </MenuItem>
                                        )
                                    )}
                                </Select>
                                {error && (
                                    <FormHelperText error>
                                        {error.message}
                                    </FormHelperText>
                                )}
                            </FormControl>
                        </ContentInput>
                    )}
                />

                <Controller
                    name="invoice_payment_methods"
                    control={control}
                    render={({
                        field: { onChange, value },
                        fieldState: { error },
                    }) => (
                        <ContentInput>
                            <FormControl fullWidth size="small">
                                <InputLabel id="create-billing-payment-methods-label">
                                    Tipo de pagamento
                                </InputLabel>
                                <Select
                                    labelId="create-billing-payment-methods-label"
                                    id="create-billing-payment-methods"
                                    value={value ?? ""}
                                    label="Tipo de pagamento"
                                    onChange={onChange}
                                >
                                    <MenuItem value="">
                                        {`Padrão (${defaultPaymentLabel})`}
                                    </MenuItem>
                                    {INVOICE_PAYMENT_METHOD_OPTIONS.map(
                                        (option) => (
                                            <MenuItem
                                                key={option.value}
                                                value={option.value}
                                            >
                                                {option.label}
                                            </MenuItem>
                                        )
                                    )}
                                </Select>
                                <FormHelperText>
                                    {error?.message ||
                                        "Se não alterar, usa o padrão do cliente/configurações."}
                                </FormHelperText>
                            </FormControl>
                        </ContentInput>
                    )}
                />

                <div className="buttonsContainer">
                    <ButtonGeneric
                        onclick={handleClearDatas}
                        buttonColor="var(--cancel)"
                        text="CANCELAR"
                        className="cancelButton"
                    />
                    <LoadingButton
                        title="Salvar"
                        loading={isLoadingButton}
                        type="submit"
                        variant="contained"
                    >
                        Criar fatura
                    </LoadingButton>
                </div>
            </Container>
        </Modal>
    );
}

export default ModalCreateMonthlyBilling;
